import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  DollarSign,
  ShoppingBag,
  TrendingUp,
  Wallet,
  Loader2,
  Download,
  ChevronRight,
} from "lucide-react";

import Header from "./Header";
import Footer from "./Footer";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const fmtMoney = (v) =>
  typeof v === "number"
    ? v.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      })
    : "$0.00";

const fmtInt = (v) => (typeof v === "number" ? v.toLocaleString("en-US") : "0");

const MONTH_LABEL = (key) => {
  if (!key || key === "unknown") return "Unknown";
  try {
    const [y, m] = key.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleString("en-US", { month: "long", year: "numeric" });
  } catch {
    return key;
  }
};

const STATUS_LABEL = {
  paid: "Paid",
  submitted: "Submitted",
  confirmed: "Confirmed",
  awaiting_payment: "Awaiting Payment",
  pending_approval: "Pending Approval",
  pending: "Pending",
  draft: "Draft",
  rejected: "Rejected",
  cancelled: "Cancelled",
  unknown: "Unknown",
};

const TYPE_LABEL = {
  stripe: "Stripe / Card",
  self_pay_invoice: "Self-Pay Invoice",
  agency_po: "Agency PO",
  agency_cash: "Agency Cash",
  cash: "Cash",
  unknown: "Unknown",
};

// ---- Date preset helpers (all client-local dates, formatted YYYY-MM-DD) ----
const isoDay = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const buildPreset = (key) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (key) {
    case "this_month":
      return { from: isoDay(new Date(y, m, 1)), to: isoDay(new Date(y, m + 1, 0)) };
    case "last_month":
      return { from: isoDay(new Date(y, m - 1, 1)), to: isoDay(new Date(y, m, 0)) };
    case "last_30":
      return { from: isoDay(new Date(y, m, now.getDate() - 29)), to: isoDay(now) };
    case "ytd":
      return { from: `${y}-01-01`, to: isoDay(now) };
    case "last_year":
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case "all":
    default:
      return { from: "", to: "" };
  }
};

const PRESETS = [
  { key: "all", label: "All Time" },
  { key: "ytd", label: "YTD" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "last_30", label: "Last 30 Days" },
  { key: "last_year", label: "Last Year" },
];

const StatCard = ({ label, value, sub, icon: Icon, accent = "teal", testId, onClick }) => {
  const accents = {
    teal: "bg-brand-teal/10 text-brand-teal",
    gold: "bg-brand-gold/15 text-brand-gold-dark",
    green: "bg-brand-green/15 text-brand-green",
    charcoal: "bg-brand-charcoal/8 text-brand-charcoal",
  };
  const clickable = typeof onClick === "function";
  const Wrapper = clickable ? "button" : "div";
  return (
    <Wrapper
      data-testid={testId}
      onClick={clickable ? onClick : undefined}
      type={clickable ? "button" : undefined}
      className={`rounded-2xl bg-white border border-black/5 p-4 sm:p-5 shadow-sm text-left w-full ${
        clickable
          ? "cursor-pointer transition-all hover:border-brand-teal/50 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal"
          : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
            {label}
          </p>
          <p className="mt-1.5 font-display font-black text-[22px] sm:text-[30px] tracking-tight text-brand-charcoal leading-none truncate">
            {value}
          </p>
          {sub ? (
            <p className="mt-1.5 text-xs text-brand-charcoal/60 truncate">{sub}</p>
          ) : null}
        </div>
        <span
          className={`inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full shrink-0 ${accents[accent]}`}
        >
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.25} />
        </span>
      </div>
      {clickable ? (
        <p className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] text-brand-teal">
          View orders <ChevronRight className="h-3 w-3" />
        </p>
      ) : null}
    </Wrapper>
  );
};

const SectionCard = ({ title, subtitle, children, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl bg-white border border-black/5 shadow-sm overflow-hidden"
  >
    <div className="px-4 sm:px-5 py-3.5 border-b border-black/5 bg-brand-charcoal/[0.02]">
      <h2 className="font-display font-bold text-base sm:text-lg text-brand-charcoal">
        {title}
      </h2>
      {subtitle ? (
        <p className="text-xs sm:text-sm text-brand-charcoal/60 mt-0.5">{subtitle}</p>
      ) : null}
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const BreakdownTable = ({ rows, labelMap, testIdPrefix, onRowClick }) => {
  if (!rows?.length) {
    return (
      <p className="text-sm text-brand-charcoal/50 italic">
        No orders in this range.
      </p>
    );
  }
  const totalGross = rows.reduce((s, r) => s + (r.gross || 0), 0);
  const clickable = typeof onRowClick === "function";
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-brand-charcoal/50 border-b border-black/5">
            <th className="py-2 pr-3 font-semibold">Bucket</th>
            <th className="py-2 px-3 font-semibold text-right">Orders</th>
            <th className="py-2 px-3 font-semibold text-right">Gross</th>
            <th className="py-2 pl-3 font-semibold text-right">% of Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const pct = totalGross > 0 ? (r.gross / totalGross) * 100 : 0;
            const label = labelMap[r.key] || r.key;
            return (
              <tr
                key={r.key}
                data-testid={`${testIdPrefix}-row-${r.key}`}
                onClick={clickable ? () => onRowClick(r.key) : undefined}
                className={`border-b border-black/5 last:border-b-0 ${
                  clickable
                    ? "cursor-pointer hover:bg-brand-teal/[0.04] transition-colors"
                    : ""
                }`}
              >
                <td className="py-2.5 pr-3 font-medium text-brand-charcoal">
                  <span className="inline-flex items-center gap-1.5">
                    {label}
                    {clickable ? (
                      <ChevronRight className="h-3 w-3 text-brand-charcoal/40" />
                    ) : null}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums text-brand-charcoal/80">
                  {fmtInt(r.count)}
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-brand-charcoal">
                  {fmtMoney(r.gross)}
                </td>
                <td className="py-2.5 pl-3 text-right tabular-nums text-brand-charcoal/60">
                  {pct.toFixed(1)}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const MonthlyTable = ({ rows, onRowClick }) => {
  if (!rows?.length) {
    return (
      <p className="text-sm text-brand-charcoal/50 italic">
        No orders in this range.
      </p>
    );
  }
  const clickable = typeof onRowClick === "function";
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-brand-charcoal/50 border-b border-black/5">
            <th className="py-2 pr-3 font-semibold">Month</th>
            <th className="py-2 px-3 font-semibold text-right">Orders</th>
            <th className="py-2 px-3 font-semibold text-right">Gross</th>
            <th className="py-2 pl-3 font-semibold text-right">Paid Gross</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.key}
              data-testid={`monthly-row-${r.key}`}
              onClick={clickable ? () => onRowClick(r.key) : undefined}
              className={`border-b border-black/5 last:border-b-0 ${
                clickable
                  ? "cursor-pointer hover:bg-brand-teal/[0.04] transition-colors"
                  : ""
              }`}
            >
              <td className="py-2.5 pr-3 font-medium text-brand-charcoal">
                <span className="inline-flex items-center gap-1.5">
                  {MONTH_LABEL(r.key)}
                  {clickable ? (
                    <ChevronRight className="h-3 w-3 text-brand-charcoal/40" />
                  ) : null}
                </span>
              </td>
              <td className="py-2.5 px-3 text-right tabular-nums text-brand-charcoal/80">
                {fmtInt(r.count)}
              </td>
              <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-brand-charcoal">
                {fmtMoney(r.gross)}
              </td>
              <td className="py-2.5 pl-3 text-right tabular-nums text-brand-green">
                {fmtMoney(r.paid_gross || 0)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function SalesReportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const authChecking = user === null;
  const isAdmin = user && user.role === "admin";

  // Default preset: All Time — matches how Philip framed the original question.
  const initialRange = buildPreset("all");
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);
  const [activePreset, setActivePreset] = useState("all");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchReport = useCallback(async (from, to) => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const res = await api.get("/admin/reports/sales", { params });
      setData(res.data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load sales report",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePreset = (key) => {
    const r = buildPreset(key);
    setActivePreset(key);
    setDateFrom(r.from);
    setDateTo(r.to);
    fetchReport(r.from, r.to);
  };

  const handleCustomApply = () => {
    setActivePreset("custom");
    fetchReport(dateFrom, dateTo);
  };

  const rangeLabel = useMemo(() => {
    if (!dateFrom && !dateTo) return "All time";
    if (dateFrom && dateTo) return `${dateFrom} → ${dateTo}`;
    return dateFrom ? `From ${dateFrom}` : `Through ${dateTo}`;
  }, [dateFrom, dateTo]);

  // ---- Drill-down: navigate to Admin Orders with pre-applied filters ----
  // Multi-status filters (e.g. "Paid Revenue" = paid|submitted|confirmed) can't
  // be expressed by the Orders tab's single-select dropdown, so we fall back
  // to just carrying the date range and let the admin narrow further there.
  const drillTo = useCallback(
    ({ status, type, from, to } = {}) => {
      const params = new URLSearchParams();
      params.set("tab", "orders");
      if (status) params.set("status", status);
      if (type) params.set("type", type);
      const f = from !== undefined ? from : dateFrom;
      const t = to !== undefined ? to : dateTo;
      if (f) params.set("from", f);
      if (t) params.set("to", t);
      navigate(`/admin/orders?${params.toString()}`);
    },
    [navigate, dateFrom, dateTo],
  );

  // A YYYY-MM key → { from, to } for the first/last day of that month.
  const monthRange = (key) => {
    if (!key || key === "unknown") return { from: dateFrom, to: dateTo };
    const [y, m] = key.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    return { from: isoDay(first), to: isoDay(last) };
  };

  const exportCsv = () => {
    if (!data) return;
    const lines = [];
    lines.push("Sales Report");
    lines.push(`Range,${rangeLabel}`);
    lines.push("");
    lines.push("Totals");
    lines.push(`Gross,${data.totals.gross}`);
    lines.push(`Paid Gross,${data.totals.paid_gross}`);
    lines.push(`Pipeline Gross,${data.totals.pipeline_gross}`);
    lines.push(`Orders,${data.totals.orders}`);
    lines.push(`Paid Orders,${data.totals.paid_orders}`);
    lines.push(`Avg Order Value,${data.totals.avg_order_value}`);
    lines.push("");
    lines.push("Monthly");
    lines.push("Month,Orders,Gross,Paid Gross");
    (data.monthly || []).forEach((r) => {
      lines.push(
        `${MONTH_LABEL(r.key)},${r.count},${r.gross},${r.paid_gross || 0}`,
      );
    });
    lines.push("");
    lines.push("By Status");
    lines.push("Status,Orders,Gross");
    (data.by_status || []).forEach((r) => {
      lines.push(`${STATUS_LABEL[r.key] || r.key},${r.count},${r.gross}`);
    });
    lines.push("");
    lines.push("By Type");
    lines.push("Type,Orders,Gross");
    (data.by_type || []).forEach((r) => {
      lines.push(`${TYPE_LABEL[r.key] || r.key},${r.count},${r.gross}`);
    });
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sales-report-${(dateFrom || "all")}-${(dateTo || "now")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand-teal" />
      </div>
    );
  }
  if (!isAdmin) {
    return <Navigate to="/login" replace />;
  }

  const totals = data?.totals || {
    gross: 0,
    paid_gross: 0,
    pipeline_gross: 0,
    orders: 0,
    paid_orders: 0,
    avg_order_value: 0,
  };

  return (
    <div className="min-h-screen bg-brand-cream/40">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Breadcrumb + title */}
        <div className="flex items-center gap-2 mb-4 text-sm">
          <Link
            to="/admin/orders"
            data-testid="back-to-admin-link"
            className="inline-flex items-center gap-1.5 text-brand-charcoal/60 hover:text-brand-teal transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Admin
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <h1
              data-testid="sales-report-title"
              className="font-display font-black text-3xl sm:text-4xl tracking-tight text-brand-charcoal"
            >
              Sales Report
            </h1>
            <p className="text-sm text-brand-charcoal/60 mt-1">
              Range: <span className="font-semibold">{rangeLabel}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            disabled={!data || loading}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-4 py-2 text-sm font-semibold hover:bg-brand-charcoal/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed self-start sm:self-auto"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>

        {/* Preset filters */}
        <div className="rounded-2xl bg-white border border-black/5 shadow-sm p-4 sm:p-5 mb-6">
          <div className="flex flex-wrap gap-2 mb-4">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => handlePreset(p.key)}
                data-testid={`preset-${p.key}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  activePreset === p.key
                    ? "bg-brand-teal text-white border-brand-teal"
                    : "bg-white text-brand-charcoal border-black/10 hover:border-brand-teal/50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-brand-charcoal/60 mb-1.5">
                From
              </label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setActivePreset("custom");
                }}
                data-testid="date-from-input"
              />
            </div>
            <div className="flex-1">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-brand-charcoal/60 mb-1.5">
                To
              </label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setActivePreset("custom");
                }}
                data-testid="date-to-input"
              />
            </div>
            <button
              type="button"
              onClick={handleCustomApply}
              data-testid="apply-custom-range-btn"
              className="rounded-full bg-brand-teal text-white px-5 py-2 text-sm font-semibold hover:bg-brand-teal/90 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Loading overlay hint */}
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-brand-charcoal/60 mb-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading report…
          </div>
        ) : null}

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <StatCard
            label="Gross Sales"
            value={fmtMoney(totals.gross)}
            sub="Paid + pipeline"
            icon={DollarSign}
            accent="teal"
            testId="stat-gross"
            onClick={() => drillTo({})}
          />
          <StatCard
            label="Paid Revenue"
            value={fmtMoney(totals.paid_gross)}
            sub={`${fmtInt(totals.paid_orders)} paid orders`}
            icon={Wallet}
            accent="green"
            testId="stat-paid"
            onClick={() => drillTo({ status: "paid" })}
          />
          <StatCard
            label="Pipeline"
            value={fmtMoney(totals.pipeline_gross)}
            sub="Unpaid / pending"
            icon={TrendingUp}
            accent="gold"
            testId="stat-pipeline"
            onClick={() => drillTo({ status: "awaiting_payment" })}
          />
          <StatCard
            label="Orders"
            value={fmtInt(totals.orders)}
            sub={`Avg ${fmtMoney(totals.avg_order_value)}`}
            icon={ShoppingBag}
            accent="charcoal"
            testId="stat-orders"
            onClick={() => drillTo({})}
          />
        </div>

        {/* Monthly + breakdowns */}
        <div className="grid grid-cols-1 gap-6">
          <SectionCard
            title="Monthly Breakdown"
            subtitle="Gross and paid-only revenue by month — tap a row to see orders"
            testId="section-monthly"
          >
            <MonthlyTable
              rows={data?.monthly || []}
              onRowClick={(key) => {
                const r = monthRange(key);
                drillTo({ from: r.from, to: r.to });
              }}
            />
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionCard
              title="By Order Status"
              subtitle="Tap a row to see orders in that stage"
              testId="section-by-status"
            >
              <BreakdownTable
                rows={data?.by_status || []}
                labelMap={STATUS_LABEL}
                testIdPrefix="status"
                onRowClick={(key) => drillTo({ status: key })}
              />
            </SectionCard>
            <SectionCard
              title="By Payment Type"
              subtitle="Tap a row to see orders of that type"
              testId="section-by-type"
            >
              <BreakdownTable
                rows={data?.by_type || []}
                labelMap={TYPE_LABEL}
                testIdPrefix="type"
                onRowClick={(key) => drillTo({ type: key })}
              />
            </SectionCard>
          </div>
        </div>

        <p className="text-xs text-brand-charcoal/50 mt-8">
          Gross = sum of every order in the range regardless of status. Paid
          Revenue counts orders in status <em>paid</em>, <em>submitted</em>, or{" "}
          <em>confirmed</em> — orders in <em>draft</em>,{" "}
          <em>awaiting_payment</em>, <em>pending_approval</em>, or{" "}
          <em>rejected</em> are excluded from Paid.
        </p>
      </main>
      <Footer />
    </div>
  );
}
